import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, ExternalLink } from 'lucide-react';
import { portal } from '../api';
import StatusBadge from '../components/StatusBadge';
import ChangePlanModal from '../components/ChangePlanModal';
import type { Contract } from '../types';

export default function ContractsPage() {
  const qc = useQueryClient();
  const [changingPlan, setChangingPlan] = useState<Contract | null>(null);
  const [error, setError] = useState('');

  const { data: contracts, isLoading } = useQuery({
    queryKey: ['portal', 'contracts'],
    queryFn: portal.contracts,
  });

  const { data: products } = useQuery({
    queryKey: ['portal', 'products'],
    queryFn: portal.products,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => portal.cancelContract(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal', 'contracts'] }),
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Kündigung fehlgeschlagen.';
      setError(msg);
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3 animate-pulse">
        {[1, 2].map((i) => (
          <div key={i} className="h-28 bg-slate-100 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!contracts?.length) {
    return (
      <div className="bg-surface border border-slate-200 rounded-xl p-10 text-center">
        <FileText className="mx-auto text-slate-300 mb-3" size={40} />
        <p className="text-slate-500">Sie haben noch keine aktiven Verträge.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Meine Verträge</h1>
        <p className="text-sm text-slate-500">Laufende Produkte verwalten, wechseln oder kündigen.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {contracts.map((contract) => {
          const url = contract.domain
            ? `https://${contract.domain}`
            : contract.subdomain
              ? `https://${contract.subdomain}.juni.com`
              : null;
          const cancelled =
            contract.status === 'CANCELLATION_SCHEDULED' || contract.status === 'TERMINATED';
          const productPlans =
            products?.find((p) => p.id === contract.productId)?.plans ?? [];

          return (
            <div key={contract.id} className="bg-surface border border-slate-200 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                  {contract.product.imageUrl && (
                    <img
                      src={contract.product.imageUrl}
                      alt=""
                      className="w-11 h-11 rounded-lg object-cover border border-slate-200"
                    />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-semibold text-slate-900">{contract.product.name}</h2>
                      <StatusBadge status={contract.status} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                      {contract.plan && <span>Tarif: {contract.plan.name}</span>}
                      {contract.plan?.monthlyPrice && (
                        <span>€{contract.plan.monthlyPrice}/Monat</span>
                      )}
                      {contract.deployedAt && (
                        <span>
                          Aktiv seit {new Date(contract.deployedAt).toLocaleDateString('de-DE')}
                        </span>
                      )}
                      {contract.terminationDate && (
                        <span className="text-amber-700">
                          Endet am{' '}
                          {new Date(contract.terminationDate).toLocaleDateString('de-DE')}
                        </span>
                      )}
                    </div>
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 mt-2"
                      >
                        {url.replace('https://', '')} <ExternalLink size={11} />
                      </a>
                    )}
                  </div>
                </div>

                {!cancelled && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button
                      onClick={() => setChangingPlan(contract)}
                      disabled={productPlans.length < 2}
                      className="text-xs font-medium border border-slate-300 text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg disabled:opacity-40"
                    >
                      Tarif wechseln
                    </button>
                    <button
                      onClick={() => {
                        setError('');
                        if (
                          confirm(
                            `Vertrag "${contract.product.name}" wirklich kündigen? Er läuft bis zum Ende der aktuellen Abrechnungsperiode weiter.`,
                          )
                        ) {
                          cancelMutation.mutate(contract.id);
                        }
                      }}
                      disabled={cancelMutation.isPending}
                      className="text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg disabled:opacity-40"
                    >
                      Kündigen
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {changingPlan && (
        <ChangePlanModal
          contract={changingPlan}
          plans={products?.find((p) => p.id === changingPlan.productId)?.plans ?? []}
          onClose={() => setChangingPlan(null)}
        />
      )}
    </div>
  );
}
